import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Checkbox from "components/Input/Checkbox/Checkbox";
import s from "./ColumnFilter.module.scss";
import { DragIndicator } from "@mui/icons-material";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import TextMAS from "components/TextMAS/TextMAS";
import { useTranslation } from "react-i18next";
import Divider from "components/Divider/Divider";

const LOCAL_STORAGE_KEY = "column-display";
const LOCALE_KEY = "component.edit.columns.";

const getColumnFilterState = (columns) => {
  return [...columns].map((column) => ({
    ...column,
    hidden: column.initHidden,
  }));
};

const getColumnsArray = (columns) => {
  return [...columns].map((column) => ({
    index: column.index,
    key: column.key,
    hidden: column.hidden,
  }));
};

const ColumnFilter = ({
  columnDisplayDialog,
  dialogOncloseFn,
  columns,
  columnsArray,
  setColumnsArray,
  rememberPreference,
}) => {
  const { t } = useTranslation();

  const [hasLoadPreference, setHasLoadPreference] = useState(false);

  // this state contains the realtime order of dragged items
  const [dragColumns, setDragColumns] = useState(getColumnFilterState(columns));
  const [dragItem, setDragItem] = useState();

  const resetColumn = () => {
    setDragColumns(getColumnFilterState(columns));
    setColumnsArray(getColumnsArray(getColumnFilterState(columns)));
  };

  const filterOnClick = (column) => {
    const newDragColumns = [...dragColumns];
    const replaceIndex = [...dragColumns].findIndex(
      (e) => e.key === column.key,
    );
    newDragColumns[replaceIndex] = {
      ...newDragColumns[replaceIndex],
      hidden: !newDragColumns[replaceIndex].hidden,
    };

    setDragColumns(newDragColumns);
    setColumnsArray(getColumnsArray(newDragColumns));
  };

  // const toggleAllColumn = (value) => {
  //   const newDragColumns = dragColumns.map((column) => ({
  //     ...column,
  //     hidden: value,
  //   }));
  //   setDragColumns(newDragColumns);
  //   setColumnsArray(getColumnsArray(newDragColumns));
  // };

  if (rememberPreference && setColumnsArray) {
    const updateBrowserPreference = (key) => {
      let columnDisplayState;
      try {
        columnDisplayState = JSON.parse(
          localStorage.getItem(LOCAL_STORAGE_KEY),
        );
      } catch (e) {
        console.log(e);
      } finally {
        const preferenceArray = dragColumns.map((column) => {
          return {
            index: column.index,
            key: column.key,
            hidden: Boolean(column.hidden),
          };
        });

        localStorage.setItem(
          LOCAL_STORAGE_KEY,
          JSON.stringify({ ...columnDisplayState, [key]: preferenceArray }),
        );
      }
    };

    const readBrowserPreference = (key) => {
      let preferenceArray;
      let columnDisplayState;
      try {
        columnDisplayState = JSON.parse(
          localStorage.getItem(LOCAL_STORAGE_KEY),
        );
        if (columnDisplayState) preferenceArray = columnDisplayState?.[key];
      } catch (e) {
        console.log(e);
      } finally {
        const isValidPreference =
          preferenceArray &&
          columns.length === preferenceArray.length &&
          [...preferenceArray].every((pref) =>
            columns.some((column) => column.key === pref.key),
          );
        if (preferenceArray && isValidPreference) {
          const sortedColumnsArray = getColumnFilterState(columns)
            .sort(
              (a, b) =>
                preferenceArray.map((e) => e.key).indexOf(a.key) -
                preferenceArray.map((e) => e.key).indexOf(b.key),
            )
            .map((column) => ({
              ...column,
              hidden: preferenceArray.some((e) => {
                if (e.key === column.key) {
                  return e.hidden;
                }
                return false;
              }),
            }));
          setDragColumns(sortedColumnsArray);
          setColumnsArray(getColumnsArray(sortedColumnsArray));
        } else {
          setColumnsArray(getColumnsArray(getColumnFilterState(columns)));
        }
      }
    };

    useEffect(() => {
      readBrowserPreference(rememberPreference);
      setHasLoadPreference(true);
    }, []);

    useEffect(() => {
      if (hasLoadPreference) {
        updateBrowserPreference(rememberPreference);
      }
    }, [columnsArray]);
  }

  const handleDragStart = (index) => {
    setDragItem(index);
  };

  const handleDragEnd = () => {
    setColumnsArray(getColumnsArray(dragColumns));
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    const newOrder = [...dragColumns];
    const item = newOrder[dragItem];
    newOrder.splice(dragItem, 1);
    newOrder.splice(index, 0, item);
    setDragItem(index);
    setDragColumns(newOrder);
  };

  return (
    <Dialog
      open={columnDisplayDialog.open}
      onClose={dialogOncloseFn}
      slotProps={{ backdrop: { sx: { backgroundColor: "transparent" } } }}
      className={s.columnFilter}
      sx={{
        "& .MuiDialog-container": {
          position: "absolute",
          right: 0,
          display: "flex",
          alignItems: "flex-start",
          top: columnDisplayDialog.position,
        },
      }}
    >
      <div className={s["column-filter-container"]}>
        <div className={s["column-filter-top-container"]}>
          {/* <Checkbox
              oncheckFn={(e) => {
                if (e.target.checked) {
                  toggleAllColumn(false);
                } else {
                  toggleAllColumn(true);
                }
              }}
              checked={dragColumns.every((column) => !column.hidden)}
              indeterminate={dragColumns.some((column) => !column.hidden)}
            /> */}
          <TextMAS fontWeight="medium">{t(LOCALE_KEY + "title")}</TextMAS>
          <ButtonMAS variant="text-only" smallWidth onClick={resetColumn}>
            {t(LOCALE_KEY + "reset")}
          </ButtonMAS>
        </div>
        <Divider />
        <div className={s["column-filter-content-container"]}>
          {dragColumns.map((column, index) => {
            return (
              <div
                key={column.key}
                className={s["item-filter"]}
                onClick={() => filterOnClick(column)}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragEnd={() => handleDragEnd()}
                onDragEnter={(e) => handleDragEnter(e, index)}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
              >
                <div className={s["item-icon"]}>
                  <DragIndicator sx={{ fontSize: 20 }} />
                </div>
                <div className={s["item-container"]}>
                  <Checkbox
                    oncheckFn={() => {}}
                    checked={!dragColumns[index].hidden}
                  />
                  <TextMAS>
                    {column.title}
                    {column.indicator && <sup>{column.indicator}</sup>}
                  </TextMAS>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Dialog>
  );
};

export default ColumnFilter;
